import type { Map as ImMap } from 'immutable';
import * as React from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { FieldAttributes } from 'formik';
import { Field } from 'formik';

import LockOutlined from '@mui/icons-material/LockOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import type { Business } from 'app-components/flux/business/businessTypes';
import { getId } from 'app-components/flux/core/resourceStoreTypes';
import FormikTextField from 'components/Formik/TextField';
import UsageAndEntityIcon from 'components/UsageAndEntityIcon';
import { makeStyles } from 'tss-react/mui';

type UsageAndEntityOption = {
  divider?: boolean;
  icon?: React.ReactNode;
  isSubheader?: boolean;
  key: string;
  label: string;
  value: string;
};

type GetMenuItemsProps = {
  onClick?: (e: React.MouseEvent<HTMLLIElement, MouseEvent>, id: 'PERSONAL' | string) => void;
  showPersonal?: boolean;
};

type UsageAndEntitySelectorProps = {
  businessesById: ImMap<string, Business>;
  name: string;
  readonly?: boolean;
  hidePersonal?: boolean;
} & FieldAttributes<any>;

const useStyles = makeStyles()((theme) => ({
  selectReadOnly: {
    display: 'flex',
    borderRadius: '12px',
    color: 'inherit',
    backgroundColor: theme.palette.background.paper,
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.background.paper,
      '& .Mui-disabled': {
        WebkitTextFillColor: theme.palette.text.primary,
        color: theme.palette.text.primary,
      },
    },
    '& .Mui-disabled': {
      opacity: 1,
      backgroundColor: theme.palette.background.paper,
      color: 'inherit',
    },
  },
}));

/*
 * Display primary business first and then the remaining businesses in ascending order of created date
 */
const sortByPrimaryAndCreatedDate = (a: Business, b: Business): number => {
  // Make sure Primary Account always comes first
  if (a.isDefault && !b.isDefault) return -1;
  if (!a.isDefault && b.isDefault) return 1;

  // Sort by created date
  const aDate = a.createdAt ?? '';
  const bDate = b.createdAt ?? '';

  if (aDate < bDate) return -1;
  if (aDate > bDate) return 1;
  return 0;
};

export const getUsageAndEntityOptions = (
  businessesById: ImMap<string, Business>,
  showPersonal: boolean = true,
): UsageAndEntityOption[] => {
  const businesses =
    (businessesById?.isEmpty() ?? true)
      ? null
      : businessesById
          .valueSeq()
          .filter((business) => !business.isClosed)
          .sort(sortByPrimaryAndCreatedDate)
          .toList();

  const options: UsageAndEntityOption[] = [];
  if (showPersonal) {
    options.push({
      divider: !!businesses,
      icon: <UsageAndEntityIcon usageIcon='PERSONAL' size='small' />,
      key: 'PERSONAL',
      label: 'Personal',
      value: 'PERSONAL',
    });
  }
  if (businesses) {
    if (showPersonal) {
      options.push({ key: 'sh_business', isSubheader: true, label: 'Business', value: '' });
    }
    businesses.forEach((business) => {
      const id = getId(business);
      options.push({
        icon: <UsageAndEntityIcon usageIcon={business?.taxGroup || 'BUSINESS'} color={business.color} size='small' />,
        key: id,
        label: business.name || `Business ${id}`,
        value: id,
      });
    });
  }
  return options;
};

export const getMenuItems = (
  businessesById: ImMap<string, Business>,
  { onClick, showPersonal = true }: GetMenuItemsProps = {},
) => {
  const options = getUsageAndEntityOptions(businessesById, showPersonal);
  return options.map((option) => {
    if (option.isSubheader) {
      return <ListSubheader key={option.key}>{option.label}</ListSubheader>;
    }
    return (
      <MenuItem
        divider={option.divider}
        key={option.key}
        value={option.value}
        onClick={(event) => onClick?.(event, option.value)}
      >
        <ListItemIcon>{option.icon}</ListItemIcon>
        <ListItemText primaryTypographyProps={{ component: 'span' }}>{option.label}</ListItemText>
      </MenuItem>
    );
  });
};

function UsageAndEntitySelector(props: UsageAndEntitySelectorProps) {
  const { businessesById, name, disabled, readOnly, hidePersonal, ...other } = props;
  const { classes } = useStyles();
  const theme = useTheme();

  const renderValue = (value: string) => {
    const business = businessesById?.get(value);
    const usageType = business || value === 'BUSINESS' ? 'BUSINESS' : 'PERSONAL';
    const usageName = business?.name || (usageType === 'BUSINESS' ? 'Business' : 'Personal');
    return (
      <Stack direction='row' alignSelf='center'>
        <UsageAndEntityIcon usageIcon={business?.taxGroup || value} size='xsmall' color={business?.color} />
        <Typography
          variant='paragraphRegular'
          sx={{
            display: 'block',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            width: '120px',
            marginLeft: theme.spacing(1),
          }}
        >
          {usageName}
        </Typography>
      </Stack>
    );
  };

  return (
    <Field
      component={FormikTextField}
      name={name}
      select
      size='small'
      variant='outlined'
      SelectProps={{ renderValue }}
      disabled={disabled || readOnly}
      readOnly={readOnly}
      IconComponent={readOnly ? LockOutlined : undefined}
      classes={{
        root: readOnly ? classes.selectReadOnly : undefined,
      }}
      {...other}
    >
      {disabled || readOnly ? null : getMenuItems(businessesById, { showPersonal: !hidePersonal })}
    </Field>
  );
}

export default UsageAndEntitySelector;
